// =====================================================
// ACCOUNT: Profile + Support + MCP Consent + MCP Grants + Devices + Notification Preferences
// =====================================================

import { z } from 'zod';
import { SupportMessageSummarySchema } from '@resida/api-types/support-chat';
import { ContactRequestSchema, ContactResponseSchema } from '@resida/api-types/support';
import {
  ManagerProfileResponseSchema,
  UpdateProfileBodySchema,
  RegisterDeviceSchema,
  NotificationPreferenceResponseSchema,
} from '@resida/api-types/account';
import {
  ConsentToolItemSchema,
  ConsentLoadResponseSchema,
  GrantRecordSchema,
  ToolPermissionRecordSchema,
} from '@resida/api-types/mcp';

import { apiFetch } from './api.core';

// ─── Support ──────────────────────────────────────────────────────────────────

export interface SupportAvailability {
  chatAvailable: boolean;
  nextAvailableAt?: string;
  operatorDisplayName: string;
}

export interface SupportConversationSummary {
  id: string;
  userIdentityId: string;
  userType: 'TENANT' | 'MANAGER';
  createdAt: string;
  updatedAt: string;
}

// Derived from SupportMessageSummarySchema in @resida/api-types/support-chat
export type SupportMessageSummary = z.infer<typeof SupportMessageSummarySchema>;

/**
 * Get current support chat availability
 */
export async function getSupportAvailability(): Promise<SupportAvailability> {
  return apiFetch<SupportAvailability>('/landlord/api/support/availability');
}

/**
 * Get or create the manager's support conversation
 */
export async function getSupportConversation(): Promise<SupportConversationSummary> {
  return apiFetch<SupportConversationSummary>('/landlord/api/support/conversation');
}

/**
 * Send a message in the support conversation
 */
export async function sendSupportMessage(
  content: string,
): Promise<{ message: SupportMessageSummary }> {
  return apiFetch<{ message: SupportMessageSummary }>(
    '/landlord/api/support/conversation/messages',
    { method: 'POST', body: JSON.stringify({ content }) },
  );
}

/**
 * List messages in the support conversation
 */
export async function listSupportMessages(
  cursor?: string,
): Promise<{ messages: SupportMessageSummary[]; nextCursor?: string }> {
  const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return apiFetch<{ messages: SupportMessageSummary[]; nextCursor?: string }>(
    `/landlord/api/support/conversation/messages${params}`,
  );
}

/**
 * Submit the support contact form
 */
export async function submitSupportContact(
  subject: string,
  message: string,
): Promise<z.infer<typeof ContactResponseSchema>> {
  // Derived from ContactRequestSchema / ContactResponseSchema in @resida/api-types/support
  const body: z.infer<typeof ContactRequestSchema> = { subject, message };
  return apiFetch<z.infer<typeof ContactResponseSchema>>('/landlord/api/support/contact', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ── Manager Profile ────────────────────────────────────────────────

// Derived from ManagerProfileResponseSchema in @resida/api-types/account
export type ManagerProfile = z.infer<typeof ManagerProfileResponseSchema>;

// Derived from UpdateProfileBodySchema in @resida/api-types/account
export type UpdateProfileInput = z.infer<typeof UpdateProfileBodySchema>;

export async function getManagerProfile(): Promise<ManagerProfile> {
  return apiFetch<ManagerProfile>('/landlord/api/profile');
}

export async function updateManagerProfile(data: UpdateProfileInput): Promise<ManagerProfile> {
  return apiFetch<ManagerProfile>('/landlord/api/profile', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// =====================================================
// MANAGER MCP CONSENT API
// =====================================================

// Derived from ConsentToolItemSchema in @resida/api-types/mcp
export type ConsentToolItem = z.infer<typeof ConsentToolItemSchema>;

// Derived from ConsentLoadResponseSchema in @resida/api-types/mcp — `kind` selects the MCP tool
// consent or the limited listing-extension consent (whose `capabilities` carry the BR-28 copy).
export type ConsentLoadResponse = z.infer<typeof ConsentLoadResponseSchema>;

/** `grantedToolNames` is required for an MCP approve and omitted for the listing-extension consent. */
export interface ConsentSubmitBody {
  decision: 'approve' | 'deny';
  grantedToolNames?: string[];
}

export interface ConsentResult {
  redirectUrl: string;
}

export async function getMcpConsentRequest(req: string): Promise<ConsentLoadResponse> {
  return apiFetch<ConsentLoadResponse>(`/landlord/api/oauth/consent/${req}`);
}

export async function submitMcpConsent(req: string, body: ConsentSubmitBody): Promise<ConsentResult> {
  return apiFetch<ConsentResult>(`/landlord/api/oauth/consent/${req}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// =====================================================
// MANAGER MCP GRANTS API
// =====================================================

// Derived from GrantRecordSchema in @resida/api-types/mcp — a 'listing-extension' grant has a null
// permissionsUrl (no tool permissions to manage).
export type GrantRecord = z.infer<typeof GrantRecordSchema>;

// Derived from ToolPermissionRecordSchema in @resida/api-types/mcp
export type ToolPermissionRecord = z.infer<typeof ToolPermissionRecordSchema>;

export interface GrantPermissionsResponse {
  permissions: ToolPermissionRecord[];
  clientName: string;
}

export interface ReplacePermissionsResult {
  added: string[];
  removed: string[];
}

export async function listMcpGrants(): Promise<{ grants: GrantRecord[] }> {
  return apiFetch<{ grants: GrantRecord[] }>('/landlord/api/mcp/grants');
}

export async function revokeMcpGrant(clientId: string): Promise<void> {
  return apiFetch<void>(`/landlord/api/mcp/grants/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
}

export async function listGrantPermissions(clientId: string): Promise<GrantPermissionsResponse> {
  return apiFetch<GrantPermissionsResponse>(`/landlord/api/mcp/grants/${encodeURIComponent(clientId)}/permissions`);
}

export async function replaceGrantPermissions(clientId: string, toolNames: string[]): Promise<ReplacePermissionsResult> {
  return apiFetch<ReplacePermissionsResult>(`/landlord/api/mcp/grants/${encodeURIComponent(clientId)}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ toolNames }),
  });
}

export async function grantToolPermission(clientId: string, toolName: string): Promise<{ granted: true; toolName: string }> {
  return apiFetch<{ granted: true; toolName: string }>(`/landlord/api/mcp/grants/${encodeURIComponent(clientId)}/permissions/${encodeURIComponent(toolName)}`, {
    method: 'POST',
  });
}

export async function revokeToolPermission(clientId: string, toolName: string): Promise<{ revoked: true; toolName: string }> {
  return apiFetch<{ revoked: true; toolName: string }>(`/landlord/api/mcp/grants/${encodeURIComponent(clientId)}/permissions/${encodeURIComponent(toolName)}`, {
    method: 'DELETE',
  });
}

// ─── Device Registration (PWA Push Notifications) ───────────────────────────

// Derived from RegisterDeviceSchema in @resida/api-types/account
export type RegisterDevicePayload = z.infer<typeof RegisterDeviceSchema>;

export interface DeviceRecord {
  id: string;
  platform: string;
  fcmToken: string;
  isActive: boolean;
}

export async function registerDevice(payload: RegisterDevicePayload): Promise<{ device: DeviceRecord }> {
  return apiFetch<{ device: DeviceRecord }>('/landlord/api/devices', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteDevice(deviceId: string): Promise<void> {
  await apiFetch<void>(`/landlord/api/devices/${deviceId}`, { method: 'DELETE' });
}

// ─── Notification Preferences ────────────────────────────────────────────────

// Derived from NotificationPreferenceResponseSchema in @resida/api-types/account
export type NotificationPreferences = z.infer<typeof NotificationPreferenceResponseSchema>;

export async function getNotificationPreferences(): Promise<{ preferences: NotificationPreferences }> {
  return apiFetch<{ preferences: NotificationPreferences }>('/landlord/api/notification-preferences');
}

export async function updateNotificationPreferences(
  data: Partial<Pick<NotificationPreferences, 'pushEnabled' | 'emailEnabled' | 'categoryPreferences'>>
): Promise<{ preferences: NotificationPreferences }> {
  return apiFetch<{ preferences: NotificationPreferences }>('/landlord/api/notification-preferences', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
